import { ChevronDown, Eye, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Signed-in identity and sign-out, in the app header.
 *
 * Renders nothing when login is not configured — an empty account chip on an
 * unsecured deployment would imply a protection that is not there.
 */
export function UserMenu() {
  const { user, role, authEnabled, isViewer, logout } = useAuth();

  if (!authEnabled || !user?.username) return null;

  const Icon = isViewer ? Eye : ShieldCheck;

  return (
    <div className="flex items-center gap-2">
      {isViewer && (
        <span
          className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono-data text-[10px] uppercase tracking-wider"
          style={{
            color: "var(--oim-gold)",
            border: "1px solid var(--oim-gold)",
            opacity: 0.9,
          }}
          title="This account cannot inject faults or change actions"
          data-testid="user-readonly-badge"
        >
          Read-only
        </span>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium"
            style={{ color: "var(--oim-text-secondary)", border: "1px solid var(--oim-border)" }}
            data-testid="user-menu-trigger"
          >
            <Icon className="w-4 h-4" />
            <span className="hidden md:inline">{user.username}</span>
            <ChevronDown className="w-3 h-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="bg-[var(--oim-surface)] border-[var(--oim-border)] text-[var(--oim-text)]"
        >
          <DropdownMenuLabel className="font-normal">
            <div className="text-xs font-medium">{user.username}</div>
            <div
              className="font-mono-data text-[10px] uppercase tracking-wider mt-0.5"
              style={{ color: "var(--oim-text-dim)" }}
            >
              {role}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-[var(--oim-border)]" />
          <DropdownMenuItem onClick={logout} data-testid="user-menu-logout">
            <LogOut className="w-3.5 h-3.5 mr-2" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
